import { ReconciliationConfidence, ReconciliationStatus } from '@consigli/types';
import clsx from 'clsx';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

const RECONCILIATION_STATUS_BADGE: Record<
  ReconciliationStatus,
  { labelKey: string; className: string }
> = {
  [ReconciliationStatus.ADDRESSED]: {
    labelKey: 'version-compare.reconciliation.status.addressed',
    className: 'border border-green-200 bg-green-50 text-green-800',
  },
  [ReconciliationStatus.UNADDRESSED]: {
    labelKey: 'version-compare.reconciliation.status.unaddressed',
    className: 'border border-red-200 bg-red-50 text-red-800',
  },
  [ReconciliationStatus.INDETERMINATE]: {
    labelKey: 'version-compare.reconciliation.status.indeterminate',
    className: 'border border-amber-200 bg-amber-50 text-amber-800',
  },
  [ReconciliationStatus.NA]: {
    labelKey: 'version-compare.reconciliation.status.na',
    className: 'border border-gray-200 bg-gray-50 text-gray-600',
  },
};

const CONFIDENCE_LABEL_KEYS: Record<ReconciliationConfidence, string> = {
  [ReconciliationConfidence.HIGH]: 'version-compare.reconciliation.confidence.high',
  [ReconciliationConfidence.MEDIUM]: 'version-compare.reconciliation.confidence.medium',
  [ReconciliationConfidence.LOW]: 'version-compare.reconciliation.confidence.low',
};

interface ReconciliationBadgeProps {
  status: ReconciliationStatus | null | undefined;
  confidence: ReconciliationConfidence | null | undefined;
  selected?: boolean;
}

export const ReconciliationBadge: FC<ReconciliationBadgeProps> = ({
  status,
  confidence,
  selected = false,
}) => {
  const { t } = useTranslation();
  if (!status || status === ReconciliationStatus.NA)
    return <span className={selected ? 'text-day-light-2' : 'text-gray-400'}>—</span>;
  const badge = RECONCILIATION_STATUS_BADGE[status];
  return (
    <div className="flex flex-col gap-1">
      {/* wrap-anywhere, not break-words: w-fit floors the pill at its min-content width,
          and overflow-wrap: break-word leaves that floor alone, so a single-word label
          would keep outgrowing a narrow reconciliation column instead of wrapping in it. */}
      <span
        className={`inline-flex w-fit items-center wrap-anywhere rounded-full px-3 py-1 text-xs font-medium ${badge.className}`}
      >
        {t(badge.labelKey)}
      </span>
      {confidence && (
        <span className={clsx('text-xs', selected ? 'text-day-light-2' : 'text-gray-500')}>
          {t(CONFIDENCE_LABEL_KEYS[confidence])}
        </span>
      )}
    </div>
  );
};
